<template>
  <Transition name="ai-panel">
    <section
      v-if="store.isOpen"
      id="kars-ai-panel"
      :aria-label="$gt('KARS-KI Support-Assistent')"
      class="ai-panel"
      data-testid="ai-panel"
      role="dialog"
      @keydown.esc.stop="store.close()"
    >
      <!-- Kopf: fließender Verlauf mit feiner Körnung -->
      <header class="ai-panel__head">
        <span aria-hidden="true" class="ai-panel__grain" />
        <span aria-hidden="true" class="ai-orb ai-panel__avatar"><Sparkles :size="18" /></span>
        <div class="ai-panel__titles">
          <h2 class="ai-panel__title">{{ $gt('KARS-KI') }}</h2>
          <p class="ai-panel__subtitle">
            <span class="ai-panel__dot" :class="{ 'is-busy': store.streaming, 'is-off': !store.enabled }" />
            {{ statusLine }}
          </p>
        </div>
        <button
          v-if="store.hasConversation"
          :aria-label="$gt('Neue Unterhaltung')"
          class="ai-icon-btn"
          :title="$gt('Neue Unterhaltung')"
          type="button"
          @click="store.newConversation()"
        >
          <RotateCcw :size="17" />
        </button>
        <button
          :aria-label="$gt('Schließen')"
          class="ai-icon-btn"
          data-testid="ai-close"
          type="button"
          @click="store.close()"
        >
          <X :size="19" />
        </button>
      </header>

      <!-- Verlauf -->
      <div ref="scroller" class="ai-panel__body" @scroll.passive="onScroll">
        <div v-if="!store.hasConversation" class="ai-panel__hero">
          <h3 class="ai-panel__greeting">
            <span class="ai-panel__greeting-text">{{ typedGreeting }}</span><span v-if="greetingTyping" aria-hidden="true" class="ai-panel__greeting-caret" />
          </h3>
          <p class="ai-panel__lead">{{ $gt('Ich erkläre Ihnen jede Funktion von KARS und bringe Sie mit einem Klick an die richtige Stelle.') }}</p>

          <AssistantInsights
            :insights="store.insights"
            @ask="askAboutInsight"
            @dismiss="store.dismissInsight"
            @open="openRoute"
          />

          <div v-if="store.starterSuggestions.length > 0" class="ai-panel__starters">
            <p class="ai-panel__starters-title">{{ $gt('Häufige Fragen') }}</p>
            <div class="ai-panel__chips">
              <button
                v-for="(s, i) in store.starterSuggestions"
                :key="s"
                class="ai-chip ai-rise"
                :style="{ animationDelay: `${120 + i * 60}ms` }"
                type="button"
                @click="sendSuggestion(s)"
              >
                <MessageCircleQuestion aria-hidden="true" :size="14" />
                {{ s }}
              </button>
            </div>
          </div>

          <p v-if="!store.enabled && store.contextLoaded" class="ai-panel__notice">
            <Info aria-hidden="true" :size="14" />
            {{ $gt('Der Chat ist in dieser Umgebung nicht aktiviert. Die Anleitungen finden Sie im Hilfe-Center.') }}
            <button class="ai-link" type="button" @click="openRoute({ name: 'PageHelp' })">
              {{ $gt('Hilfe-Center') }}
              <ArrowUpRight aria-hidden="true" class="ai-link__icon" :size="13" />
            </button>
          </p>
        </div>

        <template v-else>
          <AssistantMessage
            v-for="message in store.messages"
            :key="message.key"
            :message="message"
            @navigate="openRoute"
            @rate="store.rate"
          />
          <AssistantThinking
            v-if="showThinking"
            :step="store.step"
            :step-text="store.stepText"
          />
          <div v-if="store.suggestions.length > 0 && !store.streaming" class="ai-panel__chips ai-panel__chips--follow">
            <button
              v-for="(s, i) in store.suggestions"
              :key="s"
              class="ai-chip ai-rise"
              :style="{ animationDelay: `${i * 60}ms` }"
              type="button"
              @click="sendSuggestion(s)"
            >
              {{ s }}
            </button>
          </div>
        </template>
      </div>

      <Transition name="ai-fade">
        <button
          v-if="!atBottom"
          :aria-label="$gt('Nach unten')"
          class="ai-panel__to-bottom"
          type="button"
          @click="scrollToBottom(true)"
        >
          <ArrowDown :size="16" />
        </button>
      </Transition>

      <!-- Eingabe -->
      <div class="ai-panel__composer">
        <AssistantComposer
          ref="composer"
          :disabled="!store.enabled"
          :prefill="store.pendingQuestion"
          :streaming="store.streaming"
          @consumed="store.pendingQuestion = null"
          @send="send"
          @stop="store.stop()"
        />
      </div>
    </section>
  </Transition>
</template>

<script setup lang="ts">
  import type { AssistantInsight, AssistantRoute } from '@/services/assistantApi'
  import { ArrowDown, ArrowUpRight, Info, MessageCircleQuestion, RotateCcw, Sparkles, X } from 'lucide-vue-next'
  import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
  import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router'
  import { gt } from '@/i18n/gt'
  import { useAssistantStore } from '@/stores/assistantStore'
  import { useDisplayStore } from '@/stores/displayStore'
  import AssistantComposer from './AssistantComposer.vue'
  import AssistantInsights from './AssistantInsights.vue'
  import AssistantMessage from './AssistantMessage.vue'
  import AssistantThinking from './AssistantThinking.vue'
  import { prefersReducedMotion } from './useTypewriter'

  const store = useAssistantStore()
  const route = useRoute()
  const router = useRouter()
  const display = useDisplayStore()

  const scroller = ref<HTMLElement | null>(null)
  const composer = ref<InstanceType<typeof AssistantComposer> | null>(null)
  const atBottom = ref(true)

  // ── Gruß ────────────────────────────────────────────────────────────────
  const greeting = computed(() => {
    const hour = new Date().getHours()
    const name = store.firstName ? `, ${store.firstName}` : ''
    if (hour < 11) return `${gt('Guten Morgen')}${name}.`
    if (hour < 18) return `${gt('Hallo')}${name}.`
    return `${gt('Guten Abend')}${name}.`
  })
  const typedGreeting = ref('')
  const greetingTyping = ref(false)
  let greetingTimer = 0

  function typeGreeting () {
    window.clearInterval(greetingTimer)
    const full = greeting.value
    if (prefersReducedMotion()) {
      typedGreeting.value = full
      greetingTyping.value = false
      return
    }
    typedGreeting.value = ''
    greetingTyping.value = true
    let i = 0
    greetingTimer = window.setInterval(() => {
      i += 1
      typedGreeting.value = full.slice(0, i)
      if (i >= full.length) {
        window.clearInterval(greetingTimer)
        window.setTimeout(() => {
          greetingTyping.value = false
        }, 900)
      }
    }, Math.max(18, Math.min(45, 420 / full.length)))
  }

  const statusLine = computed(() => {
    if (!store.enabled && store.contextLoaded) return gt('Nur Hinweise verfügbar')
    if (store.streaming) return gt('Recherchiert und antwortet …')
    return gt('Ihr Assistent für KARS')
  })

  const showThinking = computed(() => {
    const last = store.lastAssistantMessage
    return store.streaming && !!last && last.streaming && last.content.length === 0
  })

  // ── Scrollen ────────────────────────────────────────────────────────────
  function onScroll () {
    const el = scroller.value
    if (!el) return
    atBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight < 48
  }

  function scrollToBottom (smooth = false) {
    const el = scroller.value
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' })
    atBottom.value = true
  }

  watch(() => store.messages.map(m => m.content.length + (m.streaming ? 1 : 0)).join(','), () => {
    if (atBottom.value) void nextTick(() => scrollToBottom())
  })

  watch(() => store.isOpen, open => {
    if (!open) return
    void nextTick(() => {
      typeGreeting()
      if (store.hasConversation) {
        scrollToBottom()
      } else {
        scroller.value?.scrollTo({ top: 0 })
        atBottom.value = true
      }
      if (!display.isMobile.value) composer.value?.focus()
    })
  }, { immediate: true })

  // Neue Unterhaltung: Begrüßung erneut tippen und nach oben scrollen.
  watch(() => store.hasConversation, has => {
    if (!has && store.isOpen) {
      typeGreeting()
      void nextTick(() => scroller.value?.scrollTo({ top: 0 }))
    }
  })

  watch(() => store.firstName, () => {
    if (store.isOpen && !store.hasConversation) typeGreeting()
  })

  onBeforeUnmount(() => window.clearInterval(greetingTimer))

  // ── Aktionen ────────────────────────────────────────────────────────────
  function currentPage () {
    return { routeName: String(route.name ?? ''), query: { ...route.query } as Record<string, unknown> }
  }

  function currentLocale () {
    try {
      return localStorage.getItem('kars-locale') || 'de'
    } catch {
      return 'de'
    }
  }

  function send (text: string) {
    void store.send(text, currentPage(), currentLocale())
  }

  function sendSuggestion (text: string) {
    send(text)
  }

  function askAboutInsight (insight: AssistantInsight) {
    send(`${insight.title} – ${gt('Was sollte ich dazu wissen?')}`)
  }

  function openRoute (to: RouteLocationRaw | AssistantRoute) {
    const location: RouteLocationRaw = typeof to === 'object' && to && 'name' in to && !('path' in to)
      ? { name: (to as AssistantRoute).name, query: (to as AssistantRoute).query, params: (to as AssistantRoute).params }
      : (to as RouteLocationRaw)
    void router.push(location)
    if (display.isMobile.value) store.close()
  }
</script>

<style scoped>
  .ai-panel {
    position: fixed;
    right: 24px;
    bottom: calc(24px + var(--app-fab-lift, 0px));
    z-index: var(--ai-z);
    display: grid;
    grid-template-rows: auto 1fr auto;
    width: min(420px, calc(100vw - 32px));
    height: min(720px, calc(100dvh - 48px));
    overflow: hidden;
    border-radius: var(--ai-radius);
    background: var(--ai-surface);
    border: 1px solid rgba(255, 255, 255, 0.7);
    box-shadow: var(--ai-shadow);
    backdrop-filter: blur(18px) saturate(1.3);
    -webkit-backdrop-filter: blur(18px) saturate(1.3);
    transform-origin: bottom right;
    isolation: isolate;
  }

  /* ── Kopf ── */
  .ai-panel__head {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 10px 14px 16px;
    background: var(--ai-gradient);
    background-size: 220% 220%;
    color: #fff;
    animation: ai-flow 12s ease-in-out infinite;
  }

  .ai-panel__grain {
    position: absolute;
    inset: 0;
    opacity: 0.08;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
    mix-blend-mode: overlay;
    pointer-events: none;
  }

  .ai-panel__avatar {
    width: 38px;
    height: 38px;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.25), 0 8px 20px rgba(0, 0, 0, 0.18);
  }

  .ai-panel__titles {
    flex: 1;
    min-width: 0;
  }

  .ai-panel__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1.2;
  }

  .ai-panel__subtitle {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 2px 0 0;
    font-size: 12px;
    opacity: 0.9;
  }

  .ai-panel__dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #86efac;
    box-shadow: 0 0 0 3px rgba(134, 239, 172, 0.25);
  }

  .ai-panel__dot.is-busy {
    background: #fde68a;
    box-shadow: 0 0 0 3px rgba(253, 230, 138, 0.3);
    animation: ai-breathe 1.2s ease-in-out infinite;
  }

  .ai-panel__dot.is-off {
    background: #cbd5e1;
    box-shadow: none;
  }

  /* ── Verlauf ── */
  .ai-panel__body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 16px 12px;
    overflow-y: auto;
    overscroll-behavior: contain;
    scroll-behavior: smooth;
    background:
      radial-gradient(600px 240px at 100% -40px, rgba(124, 58, 237, 0.07), transparent 60%),
      radial-gradient(500px 200px at 0% 100%, rgba(0, 118, 229, 0.06), transparent 60%);
  }

  .ai-panel__hero {
    display: grid;
    gap: 16px;
    padding-top: 6px;
  }

  .ai-panel__greeting {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.2;
    color: var(--ai-ink);
    min-height: 1.2em;
  }

  .ai-panel__greeting-text {
    background: var(--ai-gradient);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .ai-panel__greeting-caret {
    display: inline-block;
    width: 3px;
    height: 0.9em;
    margin-left: 3px;
    vertical-align: -2px;
    border-radius: 2px;
    background: var(--ai-violet);
    animation: ai-blink 1s steps(1) infinite;
  }

  .ai-panel__lead {
    margin: -6px 0 0;
    color: var(--ai-muted);
    font-size: 14px;
    line-height: 1.5;
  }

  .ai-panel__starters-title {
    margin: 0 0 8px;
    color: var(--ai-muted);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .ai-panel__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .ai-panel__chips--follow {
    padding-left: 34px;
  }

  .ai-panel__notice {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 10px 12px;
    border-radius: 12px;
    background: var(--ai-info-soft);
    color: var(--ai-info);
    font-size: 13px;
  }

  .ai-panel__to-bottom {
    position: absolute;
    right: 20px;
    bottom: 118px;
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 1px solid var(--ai-line);
    border-radius: 999px;
    background: #fff;
    color: var(--ai-indigo);
    box-shadow: 0 6px 16px rgba(16, 24, 40, 0.14);
    cursor: pointer;
  }

  /* ── Eingabe ── */
  .ai-panel__composer {
    padding: 8px 12px 12px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.9) 30%);
  }

  /* ── Übergänge ── */
  .ai-panel-enter-active {
    transition: opacity 240ms var(--ai-ease), transform 340ms var(--ai-ease);
  }

  .ai-panel-leave-active {
    transition: opacity 160ms ease, transform 200ms var(--ai-ease);
  }

  .ai-panel-enter-from,
  .ai-panel-leave-to {
    opacity: 0;
    transform: translateY(14px) scale(0.92);
  }

  .ai-fade-enter-active,
  .ai-fade-leave-active {
    transition: opacity 160ms;
  }

  .ai-fade-enter-from,
  .ai-fade-leave-to {
    opacity: 0;
  }

  @media (max-width: 599px) {
    .ai-panel {
      inset: 0;
      width: 100vw;
      height: 100dvh;
      border-radius: 0;
      border: 0;
    }

    .ai-panel-enter-from,
    .ai-panel-leave-to {
      transform: translateY(24px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-panel__head,
    .ai-panel__dot,
    .ai-panel__greeting-caret {
      animation: none !important;
    }

    .ai-panel-enter-active,
    .ai-panel-leave-active {
      transition: opacity 160ms;
    }

    .ai-panel-enter-from,
    .ai-panel-leave-to {
      transform: none;
    }

    .ai-panel__body {
      scroll-behavior: auto;
    }
  }
</style>
